import { usePurchaserSalePriceStore } from "@fero/stores";
import { UpdateSavedDeliveryInfo } from "@fero-shared/api-contracts";
import {
  Button,
  Divider,
  Grid,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type React from "react";
import { useEffect, useState } from "react";
import type { ZodError } from "zod";

interface SavedDeliveryAddressModalProps {
  isOpen: boolean;
  onClose?: () => void;
  onSubmit?: (savedDeliveryInfo: UpdateSavedDeliveryInfo) => void;
  initialData: UpdateSavedDeliveryInfo;
}

export const SavedDeliveryAddressModal: React.FC<
  SavedDeliveryAddressModalProps
> = ({ isOpen, onClose, onSubmit, initialData }) => {
  const { purchaserName } = usePurchaserSalePriceStore();
  const [savedDeliveryInfo, setSavedDeliveryInfo] =
    useState<UpdateSavedDeliveryInfo>(initialData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setSavedDeliveryInfo(initialData);
    }
  }, [initialData]);

  const createFieldNameErrorMap = (): Record<string, string> => ({
    "address.streetName": "Street Name is required",
    "address.town": "Town is required",
    "address.postCode": "Post Code is required",
    "address.country": "Country is required",
    deliveryPointName: "Delivery Point Name is required",
    "contact.name": "Contact Name is required",
  });

  const sanitizeValue = (value: undefined | string): string | null =>
    value === "" || value === undefined ? null : value;

  const handleInputChange = (
    key: keyof UpdateSavedDeliveryInfo,
    value: undefined | string,
    nestedKey?: string,
  ) => {
    const sanitizedValue = sanitizeValue(value);

    setSavedDeliveryInfo((prev) => {
      if (nestedKey && typeof prev[key] === "object" && prev[key] !== null) {
        return {
          ...prev,
          [key]: {
            ...(prev[key] as object),
            [nestedKey]: sanitizedValue,
          },
        };
      }
      return { ...prev, [key]: sanitizedValue };
    });
  };

  const isInvalidTypeError = (err: ZodError["errors"][number]): boolean =>
    err.code === "invalid_type" &&
    (err.received === "undefined" || err.received === "null") &&
    err.message !== "Invalid input";

  const validateDeliveryInfo = (data: UpdateSavedDeliveryInfo) => {
    const result = UpdateSavedDeliveryInfo.safeParse(data);

    if (!result.success) {
      const errorMessages: Record<string, string> = {};
      const fieldNameErrorMap = createFieldNameErrorMap();

      result.error.errors.forEach((err) => {
        const fullPath = err.path.join(".");

        if (isInvalidTypeError(err)) {
          errorMessages[fullPath] =
            fieldNameErrorMap[fullPath] || "This field is required";
        }
      });

      return errorMessages;
    }
    return {};
  };

  const handleSubmit = () => {
    const errors = validateDeliveryInfo(savedDeliveryInfo);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    // Clear form errors before submitting
    setFormErrors({});
    if (onSubmit) {
      onSubmit(savedDeliveryInfo);
    }
  };

  const renderTextField = (
    label: string,
    value: string | null | undefined,
    key: keyof UpdateSavedDeliveryInfo,
    nestedKey?: string,
  ) => {
    const error =
      formErrors[key] || (nestedKey && formErrors[`${key}.${nestedKey}`]);

    return (
      <TextField
        fullWidth
        label={label}
        value={value || ""}
        sx={{ mb: 1.5 }}
        onChange={(e) => handleInputChange(key, e.target.value, nestedKey)}
        error={!!error}
        helperText={error}
      />
    );
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="saved-delivery-modal-title"
      aria-describedby="saved-delivery-modal-description"
    >
      <Stack
        justifyContent="space-between"
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90vw", md: "75vw" },
          maxHeight: "90vh",
          overflowY: "auto",
          backgroundColor: (theme) => theme.palette.background.paper,
          borderRadius: 1,
          padding: 6,
          boxShadow: 24,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Typography
              variant="h6"
              fontSize={20}
              id="saved-delivery-modal-title"
              sx={{ marginBottom: 2 }}
            >
              Edit Saved Delivery Details for {purchaserName}
            </Typography>
          </Grid>
          {/* Horizontal Divider Below Title */}
          <Grid item xs={12}>
            <Divider sx={{ marginBottom: 5 }} />
          </Grid>

          {/* Address and Details Section */}
          <Grid container spacing={2}>
            {/* Delivery Address Section (Left) */}
            <Grid item xs={12} md={5.5}>
              <Typography sx={{ marginBottom: 2 }} variant="h6" gutterBottom>
                Delivery Address
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  {renderTextField(
                    "Delivery Point Name",
                    savedDeliveryInfo.deliveryPointName,
                    "deliveryPointName",
                  )}
                </Grid>
                <Grid item xs={12}>
                  {renderTextField(
                    "Street Name",
                    savedDeliveryInfo.address.streetName,
                    "address",
                    "streetName",
                  )}
                </Grid>
                <Grid item xs={12}>
                  {renderTextField(
                    "Address Line 1",
                    savedDeliveryInfo.address.line1,
                    "address",
                    "line1",
                  )}
                </Grid>
                <Grid item xs={12}>
                  {renderTextField(
                    "Address Line 2",
                    savedDeliveryInfo.address.line2,
                    "address",
                    "line2",
                  )}
                </Grid>
                <Grid item xs={6}>
                  {renderTextField(
                    "Town",
                    savedDeliveryInfo.address.town,
                    "address",
                    "town",
                  )}
                </Grid>
                <Grid item xs={6}>
                  {renderTextField(
                    "Post Code",
                    savedDeliveryInfo.address.postCode,
                    "address",
                    "postCode",
                  )}
                </Grid>
                <Grid item xs={12}>
                  {renderTextField(
                    "Country",
                    savedDeliveryInfo.address.country,
                    "address",
                    "country",
                  )}
                </Grid>
              </Grid>
            </Grid>

            {/* Vertical Divider */}
            <Grid
              item
              xs={12}
              md={1}
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  display: { xs: "none", md: "block" },
                  mx: 2,
                  height: "100%",
                  borderColor: "divider",
                }}
              />
            </Grid>

            {/* Delivery Contact and Instructions Section (Right) */}
            <Grid item xs={12} md={5.5}>
              <Typography sx={{ marginBottom: 2 }} variant="h6" gutterBottom>
                Delivery Contact
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  {renderTextField(
                    "Contact Name",
                    savedDeliveryInfo.contact?.name,
                    "contact",
                    "name",
                  )}
                </Grid>
                <Grid item xs={12}>
                  {renderTextField(
                    "Contact Email",
                    savedDeliveryInfo.contact?.email,
                    "contact",
                    "email",
                  )}
                </Grid>
                <Grid item xs={12}>
                  {renderTextField(
                    "Contact Phone Number",
                    savedDeliveryInfo.contact?.phoneNumber,
                    "contact",
                    "phoneNumber",
                  )}
                </Grid>
              </Grid>

              <Typography
                variant="h6"
                gutterBottom
                sx={{ marginTop: 2, marginBottom: 2 }}
              >
                Delivery Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Instructions"
                    value={savedDeliveryInfo.instruction || ""}
                    onChange={(e) =>
                      handleInputChange(
                        "instruction",
                        e.target.value,
                        undefined,
                      )
                    }
                    multiline={true}
                    minRows={6}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Horizontal Divider Above Actions */}
          <Grid item xs={12}>
            <Divider sx={{ marginTop: 2, marginBottom: 2 }} />
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12} style={{ textAlign: "right" }}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{ marginRight: 2 }}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSubmit}>
              Submit
            </Button>
          </Grid>
        </Grid>
      </Stack>
    </Modal>
  );
};
